import {DateTime} from 'luxon';
import {connect} from 'react-redux';

import {
  CalendarTypesEnum,
  ChangeCalendarFocusedDayMethodsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../libs/shared/core/src/services/tracking/tracking';
import {CalendarsPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {updateShouldCalendarDisplayWeekendsAction} from '../../store/data/preferences/preferencesActions';
import {getShouldCalendarDisplayWeekends} from '../../store/data/preferences/preferencesSelectors';
import {
  createOrUpdateCalendarEventDraftAction,
  shiftCalendarViewAction,
  updateCalendarFocusedDateAction,
  updateCalendarViewTypeAction,
} from '../../store/panels/calendars/calendarsPanelActions';
import {
  makeGetCalendarsParameters,
  makeGetCanCreateNewEvent,
} from '../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarViewShiftDirectionsEnum} from '../../store/panels/calendars/calendarsPanelState';
import {CalendarsViewTypesEnum} from '../../store/panels/calendars/calendarsParameters';
import {LargeCalendarsControls} from './largeCalendarsControls';

/*
 * Props.
 */

interface LargeCalendarsControlsContainerProps {
  panelId: CalendarsPanelId;
}

/*
 * Selectors.
 */

function makeGetViewType(getParameters: ReturnType<typeof makeGetCalendarsParameters>) {
  return createSelector([getParameters], (parameters) => parameters.viewType);
}

function makeGetFocusedDate(getParameters: ReturnType<typeof makeGetCalendarsParameters>) {
  return createSelector([getParameters], (parameters) => parameters.focusedDate);
}

function makeGetShouldShowRightControls(getViewType: ReturnType<typeof makeGetViewType>) {
  return createSelector([getViewType], (viewType) => viewType !== CalendarsViewTypesEnum.SCHEDULING);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LargeCalendarsControlsContainerProps>()(() => {
  const getParameters = makeGetCalendarsParameters();
  const getViewType = makeGetViewType(getParameters);
  const getFocusedDate = makeGetFocusedDate(getParameters);
  const getShouldShowRightControls = makeGetShouldShowRightControls(getViewType);

  return {
    canCreateNewEvent: makeGetCanCreateNewEvent(),
    viewType: getViewType,
    focusedDate: getFocusedDate,
    shouldCalendarDisplayWeekends: getShouldCalendarDisplayWeekends,
    shouldShowRightControls: getShouldShowRightControls,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<LargeCalendarsControlsContainerProps>()(({panelId}) => ({
  onViewTypeSelect: (viewType: CalendarsViewTypesEnum) => updateCalendarViewTypeAction(panelId, viewType),
  onTodayButtonClick: () => {
    trackInterfaceEvent(InterfaceEventsEnum.CHANGE_CALENDAR_FOCUSED_DAY, {
      method: ChangeCalendarFocusedDayMethodsEnum.TODAY_BUTTON,
      calendar_type: CalendarTypesEnum.LARGE,
    });

    return updateCalendarFocusedDateAction(panelId, DateTime.now().valueOf());
  },
  onCalendarShiftClick: (direction: CalendarViewShiftDirectionsEnum) => {
    trackInterfaceEvent(InterfaceEventsEnum.CHANGE_CALENDAR_FOCUSED_DAY, {
      method:
        direction === CalendarViewShiftDirectionsEnum.PREVIOUS
          ? ChangeCalendarFocusedDayMethodsEnum.PREVIOUS_BUTTON
          : ChangeCalendarFocusedDayMethodsEnum.NEXT_BUTTON,
      calendar_type: CalendarTypesEnum.LARGE,
    });

    return shiftCalendarViewAction(panelId, direction);
  },
  onShouldCalendarDisplayWeekendsChange: updateShouldCalendarDisplayWeekendsAction,
  onNewEventClick: () => createOrUpdateCalendarEventDraftAction(panelId),
}));

export const LargeCalendarsControlsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(LargeCalendarsControls);
